/**
 * Say how long a job has been standing where it is, in the reader's language.
 *
 * WHY THE SERVER SENDS A NUMBER AND THIS FILE MAKES THE WORDS
 *   The measurement and the sentence are two different jobs. The measurement
 *   must be one clock for everybody, so the server does it — see
 *   `backend/src/common/elapsed.ts`. The sentence belongs to whoever is
 *   reading, and this product is already read in English and in Hindi, so a
 *   server that sent "3 days 4 hrs" would have hard-coded one of them into an
 *   API both of them use.
 *
 * NOTHING HERE SPELLS OUT A FORMAT
 *   `Intl` writes "3 days" and "3 दिन" from the same call. The unit is chosen
 *   by size — minutes below an hour, hours below a day, days after that —
 *   because "4,328 minutes" is a number nobody can feel, and that choice is
 *   arithmetic rather than language.
 */

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;

/**
 * How long something has been waiting, in words, for the reader who wants the
 * figure rather than the feeling — *"for how many days/hrs"*, in his words.
 *
 *   45    → "45 minutes"
 *   180   → "3 hours 0 minutes"
 *   4328  → "3 days 0 hours"  (the smaller unit is kept even at zero, so a
 *                              column of these reads as one shape, not two)
 *
 * The unit names come from `Intl.NumberFormat`'s own unit formatting, so
 * "days" and "दिन" are its to decide. A runtime whose Intl cannot do units
 * falls back to the relative form rather than to an English word.
 */
export function elapsedExact(
  minutes: number | null | undefined,
  locale: string,
): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) {
    return '\u2014';
  }
  const m = Math.max(0, Math.floor(minutes));

  const unit = (value: number, u: 'minute' | 'hour' | 'day'): string => {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: u,
        unitDisplay: 'long',
      }).format(value);
    } catch {
      // An engine without unit formatting. Say it the relative way rather than
      // inventing an English word for a Hindi reader.
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
        -value,
        u,
      );
    }
  };

  if (m < MINUTES_IN_HOUR) return unit(m, 'minute');
  if (m < MINUTES_IN_DAY) {
    return `${unit(Math.floor(m / MINUTES_IN_HOUR), 'hour')} ${unit(m % MINUTES_IN_HOUR, 'minute')}`;
  }
  return `${unit(Math.floor(m / MINUTES_IN_DAY), 'day')} ${unit(Math.floor((m % MINUTES_IN_DAY) / MINUTES_IN_HOUR), 'hour')}`;
}
